import { apiUrl } from "../utils/api";

/**
 * Mail-Regeln (PO 03.08.2026, ausgebaut in Cluster REGELN G4/H1–H5).
 *
 * Der Delta-Poll prüft sie je eingehender Mail. Eine Regel besteht aus mehreren
 * Bedingungen mit EINER Verknüpfung (und/oder) darüber und einer Aktion:
 * entweder die Mail an einen bestehenden Vorgang hängen oder einen neuen
 * anlegen.
 *
 * `kriterium`/`wert` sind der LEGACY-SPIEGEL der ersten Bedingung — sie bleiben
 * für Bestandsdaten lesbar, sind aber nicht mehr die Wahrheit und können null
 * sein (z. B. bei einer reinen Zeitraum-Regel).
 */

export type RegelFeld = "absender" | "domain" | "betreff" | "inhalt" | "zeitraum";
export type RegelKriterium = "absender" | "domain" | "betreff";
export type RegelVerknuepfung = "und" | "oder";
export type RegelAktion = "zuweisen" | "neuer_vorgang";

export type RegelTextBedingung = {
    feld: "absender" | "domain" | "betreff" | "inhalt";
    wert: string;
};

/** „Empfangen zwischen" — Kalendertage (Europe/Berlin), beide Grenzen inklusive. */
export type RegelZeitraumBedingung = {
    feld: "zeitraum";
    /** 'YYYY-MM-DD' oder null (offene Grenze). */
    von: string | null;
    /** 'YYYY-MM-DD' oder null (offene Grenze). */
    bis: string | null;
};

export type RegelBedingung = RegelTextBedingung | RegelZeitraumBedingung;

export interface MailVorgangRegelDto {
    id: number;
    /** Sprechender Name; leer = Klartext aus den Bedingungen anzeigen. */
    name: string | null;
    /** Ziel-Vorgang; null bei Aktion „neuer Vorgang". */
    vorgangId: number | null;
    /** LEGACY-Spiegel der ersten Bedingung (kann null sein). */
    kriterium: RegelKriterium | null;
    /** LEGACY-Spiegel der ersten Bedingung (kann null sein). */
    wert: string | null;
    bedingungen: RegelBedingung[] | null;
    verknuepfung: RegelVerknuepfung;
    mailbox: string | null;
    aktion: RegelAktion;
    aktionVorgangsart: string | null;
    aktionBetreffQuelle: "mail" | "fest" | null;
    aktionBetreff: string | null;
    aktionKontaktQuelle: "erkannt" | "fest" | "ohne" | null;
    aktionKontaktId: number | null;
    aktionHauptverantwortlich: string[] | null;
    letzteAusfuehrungAt: string | null;
    letzterVorgangId: number | null;
    aktiv: boolean;
    createdByUserId: string | null;
    createdAt: string;
}

/** Klartext je Bedingungsfeld. */
export const FELD_LABEL: Record<RegelFeld, string> = {
    absender: "Absender ist",
    domain: "Absender-Domain ist",
    betreff: "Betreff enthält",
    inhalt: "Inhalt enthält",
    zeitraum: "Empfangen zwischen",
};

/**
 * Alt-Alias, solange noch Aufrufer auf die drei früheren Kriterien schauen.
 * Entfällt mit der letzten Fundstelle.
 */
export const KRITERIUM_LABEL: Record<RegelKriterium, string> = {
    absender: FELD_LABEL.absender,
    domain: FELD_LABEL.domain,
    betreff: FELD_LABEL.betreff,
};

/** Eingabe für Anlegen und Bearbeiten (Vollersetzung). */
export interface RegelEingabe {
    name?: string | null;
    /** Pflicht bei `aktion: "zuweisen"`. */
    vorgangId?: number | null;
    bedingungen?: RegelBedingung[] | null;
    verknuepfung?: RegelVerknuepfung | null;
    mailbox?: string | null;
    aktion?: RegelAktion | null;
    aktionVorgangsart?: string | null;
    aktionBetreffQuelle?: "mail" | "fest" | null;
    aktionBetreff?: string | null;
    aktionKontaktQuelle?: "erkannt" | "fest" | "ohne" | null;
    aktionKontaktId?: number | null;
    aktionHauptverantwortlich?: string[] | null;
    /** LEGACY-Eingang: EIN Kriterium statt einer Bedingungsliste. */
    kriterium?: RegelKriterium | null;
    /** LEGACY-Eingang: Wert zum Kriterium. */
    wert?: string | null;
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        credentials: "include",
        ...init,
        headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Anfrage fehlgeschlagen (${res.status})`);
    }
    return (res.status === 204 ? undefined : await res.json()) as T;
}

export const mailVorgangRegelService = {
    /** Regeln EINES Vorgangs — unabhängig davon, wer sie angelegt hat. */
    list(vorgangId: number): Promise<MailVorgangRegelDto[]> {
        return req<MailVorgangRegelDto[]>(apiUrl("mail/vorgang-regeln", { vorgangId }));
    },

    /** „Meine Regeln" — auch die ohne Vorgang (Aktion „neuer Vorgang"). */
    listEigene(): Promise<MailVorgangRegelDto[]> {
        return req<MailVorgangRegelDto[]>(apiUrl("mail/vorgang-regeln", { scope: "eigene" }));
    },

    create(input: RegelEingabe): Promise<MailVorgangRegelDto> {
        return req<MailVorgangRegelDto>(apiUrl("mail/vorgang-regeln"), {
            method: "POST",
            body: JSON.stringify(input),
        });
    },

    update(id: number, input: RegelEingabe): Promise<MailVorgangRegelDto> {
        return req<MailVorgangRegelDto>(apiUrl(`mail/vorgang-regeln/${id}`), {
            method: "PUT",
            body: JSON.stringify(input),
        });
    },

    setAktiv(id: number, aktiv: boolean): Promise<MailVorgangRegelDto> {
        return req<MailVorgangRegelDto>(apiUrl(`mail/vorgang-regeln/${id}`), {
            method: "PATCH",
            body: JSON.stringify({ aktiv }),
        });
    },

    remove(id: number): Promise<{ ok: true }> {
        return req<{ ok: true }>(apiUrl(`mail/vorgang-regeln/${id}`), { method: "DELETE" });
    },
};

export default mailVorgangRegelService;
